import { useState, useEffect, FormEvent } from "react";
import AdminLayout from "../components/AdminLayout";
import { supabase } from "../../../lib/supabase";

type Category = {
  id: string;
  name: string;
  slug: string;
  image_url?: string | null;
  created_at?: string;
};

const CATEGORY_TABLE = "ray_melo_categories";
const STORAGE_BUCKET = "bozzetti-assets";

export default function AdminCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<Category | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string>("editor");

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");

  const generateSlug = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const fetchUserRole = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      setUserRole("editor");
      return;
    }

    const { data, error } = await supabase
      .from("admin_users")
      .select("role")
      .eq("id", user.id)
      .single();

    if (error || !data?.role) {
      setUserRole("editor");
      return;
    }

    setUserRole(data.role);
  };

  const fetchCategories = async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from(CATEGORY_TABLE)
      .select("*")
      .order("created_at", { ascending: true });

    if (error) {
      console.error(error);
      alert("Erro ao carregar categorias: " + error.message);
      setCategories([]);
      setLoading(false);
      return;
    }

    setCategories(data || []);
    setLoading(false);
  };

  useEffect(() => {
    const init = async () => {
      await fetchUserRole();
      await fetchCategories();
    };

    init();
  }, []);

  const resetForm = () => {
    setName("");
    setSlug("");
    setImageUrl("");
    setImageFile(null);
    setPreview("");
    setEditItem(null);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (cat: Category) => {
    setEditItem(cat);
    setName(cat.name || "");
    setSlug(cat.slug || "");
    setImageUrl(cat.image_url || "");
    setPreview(cat.image_url || "");
    setImageFile(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleNameChange = (value: string) => {
    setName(value);

    if (!editItem) {
      setSlug(generateSlug(value));
    }
  };

  const handleFileChange = (file: File | null) => {
    setImageFile(file);

    if (!file) {
      setPreview(imageUrl || "");
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setPreview(localPreview);
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    setUploading(true);

    const safeName = file.name
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9.-]/g, "");

    const fileName = `categories/${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}-${safeName}`;

    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(fileName, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (uploadError) {
      console.error(uploadError);
      alert("Erro no upload da imagem: " + uploadError.message);
      setUploading(false);
      return null;
    }

    const { data } = supabase.storage
      .from(STORAGE_BUCKET)
      .getPublicUrl(fileName);

    setUploading(false);
    return data.publicUrl;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const finalName = name.trim();
    const finalSlug = (slug || generateSlug(name)).trim();

    if (!finalName) {
      alert("Informe o nome da categoria.");
      return;
    }

    if (!finalSlug) {
      alert("Informe o slug da categoria.");
      return;
    }

    setSaving(true);

    let finalImageUrl = imageUrl.trim();

    if (imageFile) {
      const uploadedUrl = await uploadImage(imageFile);

      if (!uploadedUrl) {
        setSaving(false);
        return;
      }

      finalImageUrl = uploadedUrl;
    }

    if (editItem) {
      const { error } = await supabase
        .from(CATEGORY_TABLE)
        .update({
          name: finalName,
          slug: finalSlug,
          image_url: finalImageUrl || null,
        })
        .eq("id", editItem.id);

      if (error) {
        console.error(error);
        alert("Erro ao atualizar categoria: " + error.message);
        setSaving(false);
        return;
      }
    } else {
      const { error } = await supabase.from(CATEGORY_TABLE).insert({
        id: crypto.randomUUID(),
        name: finalName,
        slug: finalSlug,
        image_url: finalImageUrl || null,
        created_at: new Date().toISOString(),
      });

      if (error) {
        console.error(error);
        alert("Erro ao criar categoria: " + error.message);
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    closeForm();
    await fetchCategories();
  };

  const handleDelete = async (id: string) => {
    if (userRole !== "admin") {
      alert("Você não tem permissão para excluir categorias.");
      return;
    }

    const { error } = await supabase.from(CATEGORY_TABLE).delete().eq("id", id);

    if (error) {
      console.error(error);
      alert("Erro ao excluir categoria: " + error.message);
      return;
    }

    setDeleteId(null);
    await fetchCategories();
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1
              className="text-xl font-light tracking-[0.15em] text-black uppercase"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Categorias
            </h1>
            <p className="text-[11px] tracking-wide text-black/40 mt-1">
              Gerencie as categorias de produtos da Ray Melo
            </p>
          </div>

          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-black text-white text-[11px] tracking-[0.15em] px-4 py-2.5 rounded-md hover:bg-black/80 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line text-sm" />
            </div>
            Nova Categoria
          </button>
        </div>

        {loading ? (
          <div className="text-[11px] tracking-wide text-black/40">
            Carregando...
          </div>
        ) : (
          <div className="bg-white border border-black/8 rounded-lg overflow-hidden">
            {categories.length === 0 ? (
              <div className="p-10 text-center text-[11px] tracking-wide text-black/30">
                Nenhuma categoria cadastrada
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-black/8">
                    <th className="text-left px-5 py-3 text-[10px] tracking-[0.2em] text-black/40 uppercase font-medium">
                      Imagem
                    </th>
                    <th className="text-left px-5 py-3 text-[10px] tracking-[0.2em] text-black/40 uppercase font-medium">
                      Nome
                    </th>
                    <th className="text-left px-5 py-3 text-[10px] tracking-[0.2em] text-black/40 uppercase font-medium hidden md:table-cell">
                      Slug
                    </th>
                    <th className="px-5 py-3 text-right text-[10px] tracking-[0.2em] text-black/40 uppercase font-medium">
                      Ações
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {categories.map((cat) => (
                    <tr
                      key={cat.id}
                      className="border-b border-black/5 hover:bg-black/[0.02] transition-colors"
                    >
                      <td className="px-5 py-3.5">
                        <div className="w-16 h-20 bg-[#F5F2EC] overflow-hidden border border-black/10">
                          {cat.image_url ? (
                            <img
                              src={cat.image_url}
                              alt={cat.name}
                              className="w-full h-full object-cover object-top"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[9px] text-black/25 uppercase text-center px-1">
                              Sem foto
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-5 py-3.5 text-[12px] tracking-wide text-black uppercase">
                        {cat.name}
                      </td>

                      <td className="px-5 py-3.5 text-[11px] tracking-wide text-black/40 hidden md:table-cell">
                        {cat.slug}
                      </td>

                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEdit(cat)}
                            className="w-7 h-7 flex items-center justify-center rounded text-black/40 hover:text-black hover:bg-black/5 transition-all cursor-pointer"
                            title="Editar"
                          >
                            <i className="ri-edit-line text-sm" />
                          </button>

                          {userRole === "admin" && (
                            <button
                              onClick={() => setDeleteId(cat.id)}
                              className="w-7 h-7 flex items-center justify-center rounded text-black/30 hover:text-red-500 hover:bg-red-50 transition-all cursor-pointer"
                              title="Excluir"
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-[13px] tracking-[0.15em] font-medium text-black uppercase">
                {editItem ? "Editar Categoria" : "Nova Categoria"}
              </h2>

              <button
                onClick={closeForm}
                className="w-7 h-7 flex items-center justify-center cursor-pointer hover:opacity-50"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-[10px] tracking-[0.2em] text-black/50 mb-2 uppercase">
                  Nome
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                  placeholder="Ex: BLUSAS"
                  className="w-full border-b border-black/20 bg-transparent py-2 text-sm outline-none focus:border-black transition-colors"
                />
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.2em] text-black/50 mb-2 uppercase">
                  Slug
                </label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(generateSlug(e.target.value))}
                  required
                  placeholder="Ex: blusas"
                  className="w-full border-b border-black/20 bg-transparent py-2 text-sm outline-none focus:border-black transition-colors"
                />
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.2em] text-black/50 mb-2 uppercase">
                  URL da imagem
                </label>
                <input
                  type="text"
                  value={imageUrl}
                  onChange={(e) => {
                    setImageUrl(e.target.value);
                    setPreview(e.target.value);
                    setImageFile(null);
                  }}
                  placeholder="https://..."
                  className="w-full border-b border-black/20 bg-transparent py-2 text-sm outline-none focus:border-black transition-colors"
                />
              </div>

              <div>
                <label className="block text-[10px] tracking-[0.2em] text-black/50 mb-2 uppercase">
                  Upload da foto
                </label>

                <label className="flex items-center justify-center w-full h-32 border border-dashed border-black/20 rounded-md cursor-pointer hover:border-black/40 transition-colors bg-black/[0.01]">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) =>
                      handleFileChange(e.target.files?.[0] || null)
                    }
                  />

                  <span className="text-[11px] tracking-[0.15em] text-black/40 uppercase">
                    Selecionar imagem
                  </span>
                </label>
              </div>

              {preview && (
                <div className="w-full h-56 bg-[#F5F2EC] overflow-hidden border border-black/10 rounded-md">
                  <img
                    src={preview}
                    alt="Preview"
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 border border-black/20 text-black/60 text-[11px] tracking-[0.15em] py-2.5 rounded-md hover:border-black hover:text-black transition-all cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  disabled={saving || uploading}
                  className="flex-1 bg-black text-white text-[11px] tracking-[0.15em] py-2.5 rounded-md hover:bg-black/80 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {saving || uploading
                    ? "Salvando..."
                    : editItem
                    ? "Atualizar"
                    : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteId && userRole === "admin" && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-sm p-6">
            <h2 className="text-[13px] tracking-[0.1em] font-medium text-black mb-2">
              Excluir categoria?
            </h2>

            <p className="text-[11px] tracking-wide text-black/50 mb-5">
              Essa ação remove a categoria apenas da Ray Melo.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 border border-black/20 text-black/60 text-[11px] tracking-[0.15em] py-2.5 rounded-md cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>

              <button
                onClick={() => handleDelete(deleteId)}
                className="flex-1 bg-red-500 text-white text-[11px] tracking-[0.15em] py-2.5 rounded-md hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}